<template>
  <div>
    <v-overlay :value="loading" class="align-center justify-center">
      <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="error.show">
      <p class="py-0 my-0 font-weight-black">{{ error.title }}</p>
      {{ error.message }}

      <template v-slot:action="{ attrs }">
        <v-btn color="blue" text v-bind="attrs" @click="error.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <v-toolbar flat style="background-color: #f3f3f3">
      <h3 class="grey--text">
        <v-icon left color="grey">mdi-calculator-variant</v-icon>
        Calculadora de cenários
      </h3>
      <v-spacer></v-spacer>
      <v-btn color="grey" dark small class="mr-2" @click="limparSelecao()" v-if="planoSelecionado">
        <v-icon small left>mdi-eraser</v-icon>
        Limpar
      </v-btn>
      <v-btn color="green" dark small @click="imprimirProposta()" v-if="planoSelecionado">
        <v-icon small left>mdi-printer</v-icon>
        Imprimir
      </v-btn>
    </v-toolbar>

    <v-container fluid class="pa-4">
      <v-card elevation="2" class="mb-4">
        <v-card-title class="pb-0">
          <v-icon left color="grey">mdi-account-details</v-icon>
          Informações do cliente
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="cliente.nome" label="Nome do cliente" placeholder="Digite o nome do cliente" outlined
                dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="cliente.tipo" :items="tiposNegocio" label="Tipo de negócio" outlined dense
                hide-details></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <h3 class="grey--text mb-3">
        <v-icon left color="grey">mdi-view-grid</v-icon>
        Selecione um plano
      </h3>
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3" v-for="plano in planosDisponiveis" :key="plano.value">
          <v-card :elevation="planoSelecionado && planoSelecionado.value === plano.value ? 8 : 2"
            :class="{ 'plano-ativo': planoSelecionado && planoSelecionado.value === plano.value }"
            @click="selecionarPlano(plano)" class="plano-card" style="cursor: pointer;">
            <v-card-title class="pb-1">
              <span class="text-subtitle-1 font-weight-medium">{{ plano.text }}</span>
            </v-card-title>
            <v-card-text>
              <div class="text-h5 font-weight-bold primary--text mb-2">
                {{ formatarMoeda(precosPlanos[plano.value] || 0) }}
              </div>
              <div class="text-caption grey--text">por mês</div>
            </v-card-text>
            <v-card-actions>
              <v-btn block :color="planoSelecionado && planoSelecionado.value === plano.value ? 'success' : 'primary'"
                small>
                {{ planoSelecionado && planoSelecionado.value === plano.value ? 'Selecionado' : 'Selecionar' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="planoSelecionado">
        <v-col cols="12" md="6">
          <v-card elevation="2">
            <v-card-title>
              <v-icon left color="grey">mdi-puzzle</v-icon>
              Módulos do plano
              <v-spacer></v-spacer>
              <v-chip small color="success" dark>
                {{ planoSelecionado.text }}
              </v-chip>
            </v-card-title>
            <v-card-text style="max-height: 660px; overflow-y: auto;">
              <div v-for="(modulos, categoria) in modulosPorCategoria" :key="categoria">
                <div class="categoria-header grey lighten-3 pa-2 mb-2 rounded">
                  <strong>{{ categoria }}</strong>
                </div>
                <v-checkbox v-for="modulo in modulos" :key="modulo.id" v-model="modulosSelecionados" :value="modulo.id"
                  :label="modulo.name" :disabled="modulo.mandatory" dense hide-details class="mb-1">
                  <template v-slot:label>
                    <div class="d-flex align-center justify-space-between" style="width: 100%;">
                      <span>
                        {{ modulo.name }}
                        <v-chip x-small class="ml-2" v-if="modulo.mandatory" color="orange" dark>Obrigatório</v-chip>
                        <v-chip x-small class="ml-2" v-else-if="modulosDoPlano.includes(modulo.id)" color="success"
                          dark>Incluso</v-chip>
                      </span>
                      <span class="text-caption grey--text">
                        Custo: {{ formatarMoeda(modulo.cost) }} |
                        Venda: {{ formatarMoeda(modulo.additionalPrice) }}
                      </span>
                    </div>
                  </template>
                </v-checkbox>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card elevation="2" class="mb-4">
            <v-card-title>
              <v-icon left color="grey">mdi-chart-line</v-icon>
              Análise de lucro
            </v-card-title>
            <v-card-text>
              <v-simple-table>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">Custo total:</td>
                    <td class="text-right red--text font-weight-bold">{{ formatarMoeda(calculos.custoTotal) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Venda sugerida:</td>
                    <td class="text-right green--text font-weight-bold">{{ formatarMoeda(calculos.vendaTotal) }}</td>
                  </tr>
                  <tr class="grey lighten-4">
                    <td class="font-weight-bold text-h6">Lucro bruto/mês:</td>
                    <td class="text-right orange--text font-weight-bold text-h6">{{ formatarMoeda(calculos.lucroTotal) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-card elevation="2" class="mb-4">
            <v-card-title>
              <v-icon left color="grey">mdi-cash-plus</v-icon>
              Taxas adicionais
            </v-card-title>
            <v-card-text>
              <v-text-field v-model.number="taxas.implantacao" label="Taxa de implantação" type="number" prefix="R$"
                outlined dense hide-details class="mb-3"></v-text-field>
              <v-text-field v-model.number="taxas.cadastro" label="Taxa de cadastro de produtos" type="number" prefix="R$"
                outlined dense hide-details></v-text-field>
            </v-card-text>
          </v-card>

          <v-card elevation="2">
            <v-card-title>
              <v-icon left color="grey">mdi-format-list-checks</v-icon>
              Módulos selecionados
            </v-card-title>
            <v-card-text style="max-height: 230px; overflow-y: auto;">
              <v-simple-table dense v-if="modulosSelecionadosDetalhes.length > 0">
                <thead>
                  <tr>
                    <th>Módulo</th>
                    <th class="text-right red--text">Custo</th>
                    <th class="text-right green--text">Venda</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="modulo in modulosSelecionadosDetalhes" :key="modulo.id">
                    <td>
                      {{ modulo.name }}
                      <span v-if="modulosDoPlano.includes(modulo.id)" class="green--text text-caption">(Incluso)</span>
                    </td>
                    <td class="text-right red--text">{{ formatarMoeda(modulo.cost) }}</td>
                    <td class="text-right green--text">
                      {{ modulosDoPlano.includes(modulo.id) ? formatarMoeda(0) : formatarMoeda(modulo.additionalPrice) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="text-center grey--text pa-4">
                Nenhum módulo selecionado
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card elevation="2" class="mt-4" v-if="planoSelecionado">
        <v-card-title>
          <v-icon left color="grey">mdi-compare</v-icon>
          Comparação de Planos
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="3" v-for="plano in planosDisponiveis" :key="'comp-' + plano.value">
              <v-card outlined :class="{ 'border-primary': planoSelecionado.value === plano.value }">
                <v-card-text class="text-center">
                  <div class="text-subtitle-1 font-weight-bold mb-2">{{ plano.text }}</div>
                  <div class="text-h5 font-weight-bold orange--text">
                    {{ formatarMoeda(calcularLucroPlano(plano)) }}
                  </div>
                  <div class="text-caption grey--text">lucro/mês</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-alert type="info" border="left" colored-border elevation="2" class="mt-4" v-if="!planoSelecionado">
        <v-icon left>mdi-information</v-icon>
        Selecione um plano acima para começar a montar sua proposta comercial.
      </v-alert>
    </v-container>

    <v-dialog v-model="dialogProposta" max-width="800px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-file-document</v-icon>
          Proposta comercial
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogProposta = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4" id="proposta-print">
          <div class="proposta-header mb-4">
            <h2 class="primary--text mb-1">PROPOSTA COMERCIAL</h2>
            <div class="d-flex justify-space-between">
              <span><strong>Cliente:</strong> {{ cliente.nome || 'Não informado' }} | <strong>Tipo:</strong> {{
                cliente.tipo || 'Não informado' }}</span>
              <span><strong>Data:</strong> {{ dataAtual }}</span>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div class="mb-4" v-if="planoSelecionado">
            <h3 class="primary--text mb-2">Plano</h3>
            <div class="text-h5 font-weight-bold">{{ planoSelecionado.text }}</div>
          </div>

          <div class="mb-4">
            <h3 class="primary--text mb-2">Recursos inclusos</h3>
            <v-row>
              <v-col cols="6">
                <ul>
                  <li
                    v-for="modulo in modulosSelecionadosDetalhes.slice(0, Math.ceil(modulosSelecionadosDetalhes.length / 2))"
                    :key="'p1-' + modulo.id">
                    {{ modulo.name }}
                  </li>
                </ul>
              </v-col>
              <v-col cols="6">
                <ul>
                  <li
                    v-for="modulo in modulosSelecionadosDetalhes.slice(Math.ceil(modulosSelecionadosDetalhes.length / 2))"
                    :key="'p2-' + modulo.id">
                    {{ modulo.name }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </div>

          <div class="mb-4">
            <h3 class="primary--text mb-2">Investimento mensal</h3>
            <v-simple-table>
              <tbody>
                <tr>
                  <td>Plano {{ planoSelecionado ? planoSelecionado.text : '' }}:</td>
                  <td class="text-right font-weight-bold">{{ formatarMoeda(precosPlanos[planoSelecionado ?
                    planoSelecionado.value : 0] || 0) }}</td>
                </tr>
                <tr>
                  <td>Taxa de implantação:</td>
                  <td class="text-right font-weight-bold">{{ formatarMoeda(taxas.implantacao) }}</td>
                </tr>
                <tr>
                  <td>Taxa de cadastro:</td>
                  <td class="text-right font-weight-bold">{{ formatarMoeda(taxas.cadastro) }}</td>
                </tr>
                <tr>
                  <td>Mensalidade:</td>
                  <td class="text-right font-weight-bold">{{ formatarMoeda(calculos.vendaMensalidade) }}</td>
                </tr>
                <tr class="grey lighten-3">
                  <td class="text-h6 font-weight-bold">Valor do primeiro mês:</td>
                  <td class="text-right text-h6 font-weight-bold primary--text">{{ formatarMoeda(calculos.vendaTotal) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-divider class="my-4"></v-divider>
          <div class="text-center grey--text text-caption">
            Proposta gerada em: {{ dataHoraAtual }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogProposta = false">Fechar</v-btn>
          <v-btn color="primary" @click="print()">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { responsavelGeralDados } from "../../services/api";
import { verificarSessao } from "../../utils/utils";

export default {
  name: "Calculadora",
  data() {
    return {
      loading: false,
      error: {
        show: false,
        message: "",
        title: "",
      },
      token: localStorage.getItem("token"),
      dialogProposta: false,
      cliente: {
        nome: "",
        tipo: "",
      },
      tiposNegocio: [
        "Hamburgueria",
        "Pizzaria",
        "Restaurante",
        "Loja de Roupas",
        "Loja de Alimentos",
        "Farmácia",
        "Salão de Beleza",
        "Clínica",
        "Consultório",
        "Padaria",
        "Açaí",
        "Sorveteria",
        "Cafeteria",
        "Bar",
        "Outro",
      ],
      precosPlanos: {
        0: 0,
        1: 0,
        2: 0,
        3: 0,
      },
      planoSelecionado: null,
      modulosSelecionados: [],
      taxas: {
        implantacao: 0,
        cadastro: 0,
      },
      allModules: [
        { id: 'retaguarda', name: 'Retaguarda (Base da loja)', costKey: 'valorRevenda', priceKey: null, planKey: null, category: 'Retaguarda', mandatory: true },
        { id: 'pdv', name: 'Ponto de Venda (PDV)', costKey: null, priceKey: 'preco_modulo_pdv', planKey: 'plano_modulo_pdv', category: 'Módulos' },
        { id: 'controle_mesas', name: 'Controle de Mesas', costKey: null, priceKey: 'preco_modulo_mesas', planKey: 'plano_modulo_mesas', category: 'Módulos' },
        { id: 'controle_reservas', name: 'Controle de Reservas', costKey: null, priceKey: 'preco_modulo_reservas', planKey: 'plano_modulo_reservas', category: 'Módulos' },
        { id: 'controle_fila', name: 'Controle de Fila', costKey: null, priceKey: 'preco_modulo_fila', planKey: 'plano_modulo_fila', category: 'Módulos' },
        { id: 'controle_estoque', name: 'Controle de Estoque', costKey: null, priceKey: 'preco_modulo_estoque', planKey: 'plano_modulo_estoque', category: 'Módulos' },
        { id: 'controle_financeiro', name: 'Controle Financeiro', costKey: null, priceKey: 'preco_modulo_financeiro', planKey: 'plano_modulo_financeiro', category: 'Módulos' },
        { id: 'ifood', name: 'iFood', costKey: null, priceKey: 'preco_modulo_ifood', planKey: 'plano_modulo_ifood', category: 'Integrações' },
        { id: 'programas_fidelizacao', name: 'Programas de Fidelização', costKey: null, priceKey: 'preco_modulo_marketing', planKey: 'plano_modulo_marketing', category: 'Integrações' },
        { id: 'chat_online', name: 'Chat Online', costKey: null, priceKey: 'preco_modulo_chat', planKey: 'plano_modulo_chat', category: 'Integrações' },
        { id: 'notificacao_whatsapp', name: 'Notificação WhatsApp', costKey: null, priceKey: null, planKey: null, category: 'Integrações' },
        { id: 'pix_instantaneo', name: 'Pix Instantâneo', costKey: null, priceKey: null, planKey: null, category: 'Integrações' },
        { id: 'pagamento_online', name: 'Pagamento Online', costKey: null, priceKey: null, planKey: null, category: 'Integrações' },
        { id: 'api_integracao', name: 'API de Integração', costKey: null, priceKey: 'preco_modulo_integracoes', planKey: 'plano_modulo_integracoes', category: 'Integrações' },
        { id: 'robo_atendimento', name: 'Robô de Atendimento', costKey: 'valorRobo', priceKey: 'preco_modulo_whatsapp_robo_atendimento', planKey: 'plano_modulo_whatsapp_robo_atendimento', category: 'Campanhas' },
        { id: 'campanhas_email', name: 'Campanhas por E-mail', costKey: 'valorGamaZapCampanhas', priceKey: 'preco_modulo_marketing_email_marketing', planKey: 'plano_modulo_marketing_email_marketing', category: 'Campanhas' },
        { id: 'campanhas_sms', name: 'Campanhas por SMS', costKey: null, priceKey: 'preco_modulo_marketing_sms', planKey: 'plano_modulo_marketing_sms', category: 'Campanhas' },
        { id: 'campanhas_push', name: 'Campanhas por Push', costKey: 'valorGamaZapCampanhas', priceKey: 'preco_modulo_marketing_notificacao_push', planKey: 'plano_modulo_marketing_notificacao_push', category: 'Campanhas' },
        { id: 'campanhas_whatsapp', name: 'Campanhas por WhatsApp', costKey: 'valorGamaZapCampanhas', priceKey: 'preco_modulo_marketing_whatsapp_marketing', planKey: 'plano_modulo_marketing_whatsapp_marketing', category: 'Campanhas' },
        { id: 'emissor_nfce', name: 'Emissor Fiscal (NFC-e)', costKey: 'valorFiscal', priceKey: 'preco_modulo_fiscal', planKey: 'plano_modulo_fiscal', category: 'Fiscal' },
        { id: 'emissor_nfe', name: 'Emissor Fiscal (NF-e)', costKey: 'valorFiscal', priceKey: 'preco_modulo_fiscal_nfe', planKey: 'plano_modulo_fiscal_nfe', category: 'Fiscal' },
        { id: 'distribuicao_nfe', name: 'Distribuição NF-e (DF-e)', costKey: null, priceKey: 'preco_modulo_dfe', planKey: 'plano_modulo_dfe', category: 'Fiscal' },
        { id: 'app_motoboy', name: 'App Motoboy', costKey: null, priceKey: 'preco_modulo_app_motoboy', planKey: 'plano_modulo_app_motoboy', category: 'Aplicativos' },
        { id: 'gzap', name: 'G-ZAP', costKey: null, priceKey: 'preco_modulo_gzap', planKey: 'plano_modulo_gzap', category: 'Aplicativos' },
        { id: 'gtef', name: 'G-TEF', costKey: 'valorGtef', priceKey: 'preco_modulo_gtef', planKey: 'plano_modulo_gtef', category: 'Aplicativos' },
        { id: 'gpos', name: 'G-POS', costKey: 'valorGpos', priceKey: 'preco_modulo_gpos', planKey: 'plano_modulo_gpos', category: 'Aplicativos' },
        { id: 'gestor', name: 'G-GESTOR', costKey: null, priceKey: 'preco_modulo_gestor', planKey: 'plano_modulo_gestor', category: 'Aplicativos' },
        { id: 'gtotem', name: 'G-TOTEM', costKey: 'valorGtotem', priceKey: 'preco_modulo_totem', planKey: 'plano_modulo_totem', category: 'Aplicativos' },
        { id: 'gmesas', name: 'G-MESAS', costKey: 'valorGmesas', priceKey: 'preco_modulo_gmesas', planKey: 'plano_modulo_gmesas', category: 'Aplicativos' },
        { id: 'monitor_senhas', name: 'Monitor de Senhas', costKey: null, priceKey: 'preco_modulo_senhas', planKey: 'plano_modulo_senhas', category: 'Monitores' },
        { id: 'monitor_entregas', name: 'Monitor de Entregas', costKey: null, priceKey: 'preco_modulo_entregas', planKey: 'plano_modulo_entregas', category: 'Monitores' },
        { id: 'monitor_preparo', name: 'Monitor de Preparo (KDS)', costKey: null, priceKey: 'preco_modulo_preparo', planKey: 'plano_modulo_preparo', category: 'Monitores' },
        { id: 'monitor_balancas', name: 'Monitor de Balanças', costKey: null, priceKey: 'preco_modulo_balanca', planKey: 'plano_modulo_balanca', category: 'Monitores' },
        { id: 'suporte_dedicado', name: 'Suporte Dedicado', costKey: 'valorSuporte', priceKey: 'preco_modulo_suporte_dedicado', planKey: 'plano_modulo_suporte_dedicado', category: 'Serviços' },
        { id: 'gerente_dedicado', name: 'Gerente Dedicado', costKey: null, priceKey: 'preco_modulo_gerente_dedicado', planKey: 'plano_modulo_gerente_dedicado', category: 'Serviços' },
        { id: 'dominio_proprio', name: 'Domínio Próprio', costKey: null, priceKey: 'preco_modulo_dominio_proprio', planKey: 'plano_modulo_dominio_proprio', category: 'Serviços' },
        { id: 'certificado_digital', name: 'Certificado Digital', costKey: null, priceKey: 'preco_certificado_digital', planKey: 'plano_certificado_digital', category: 'Serviços' },
        { id: 'campanhas_agendadas', name: 'Campanhas Agendadas', costKey: null, priceKey: 'preco_cron', planKey: 'plano_cron', category: 'Serviços' },
        { id: 'google_meu_negocio', name: 'Google Meu Negócio', costKey: null, priceKey: 'preco_modulo_google_meu_negocio', planKey: 'plano_modulo_google_meu_negocio', category: 'Serviços' },
        { id: 'consultoria_estrategica', name: 'Consultoria Estratégica', costKey: null, priceKey: 'preco_modulo_consultoria_estrategica', planKey: 'plano_modulo_consultoria_estrategica', category: 'Serviços' },
        { id: 'consultoria_ifood', name: 'Consultoria iFood', costKey: null, priceKey: 'preco_modulo_consultoria_ifood', planKey: 'plano_modulo_consultoria_ifood', category: 'Serviços' },
      ],
      responsavel: {},
    };
  },
  computed: {
    planosDisponiveis() {
      return this.planos;
    },
    modulosDoPlano() {
      if (!this.planoSelecionado) return [];
      const planoSelecionadoValue = this.planoSelecionado.value;
      const modulosInclusos = ['retaguarda'];

      this.allModules.forEach((modulo) => {
        if (modulo.planKey && this.responsavel[modulo.planKey] !== undefined) {
          const planoDoModulo = parseInt(this.responsavel[modulo.planKey]) || 0;
          if (planoDoModulo > 0 && planoDoModulo <= planoSelecionadoValue) {
            modulosInclusos.push(modulo.id);
          }
        }
      });

      return modulosInclusos;
    },
    modulosPorCategoria() {
      const categorias = {};
      this.allModules.forEach((modulo) => {
        const moduloComValores = this.getModuloComValores(modulo);
        if (!categorias[modulo.category]) {
          categorias[modulo.category] = [];
        }
        categorias[modulo.category].push(moduloComValores);
      });
      return categorias;
    },
    modulosSelecionadosDetalhes() {
      return this.modulosSelecionados
        .map((id) => {
          const modulo = this.allModules.find((m) => m.id === id);
          return modulo ? this.getModuloComValores(modulo) : null;
        })
        .filter((m) => m);
    },
    calculos() {
      let custoTotal = 0;
      let vendaTotal = 0;
      let vendaMensalidade = 0;

      this.modulosSelecionadosDetalhes.forEach((modulo) => {
        custoTotal += modulo.cost;
        if (!this.modulosDoPlano.includes(modulo.id)) {
          vendaTotal += modulo.additionalPrice;
        }
      });

      if (this.planoSelecionado && this.planoSelecionado.value !== 0) {
        vendaTotal += this.precosPlanos[this.planoSelecionado.value] || 0;
        vendaMensalidade += this.precosPlanos[this.planoSelecionado.value] || 0;
      }

      vendaTotal += (this.taxas.implantacao || 0) + (this.taxas.cadastro || 0);

      return {
        custoTotal,
        vendaTotal,
        lucroTotal: vendaTotal - custoTotal,
        vendaMensalidade,
      };
    },
    dataAtual() {
      return new Date().toLocaleDateString("pt-BR");
    },
    dataHoraAtual() {
      return new Date().toLocaleString("pt-BR");
    },
  },
  async created() {
    window.addEventListener("offline", () => (this.loading = true));
    window.addEventListener("online", () => (this.loading = false));
    verificarSessao(this.carregarDados);
  },
  methods: {
    setError(error) {
      this.error = {
        show: true,
        title: "Ops, falhou!",
        message: error ? error : "Falha não identificada, tente novamente.",
      };
    },
    formatarMoeda(valor) {
      return (valor || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    getModuloComValores(modulo) {
      let cost = 0;
      let additionalPrice = 0;

      if (modulo.costKey) {
        cost = this[modulo.costKey] || 0;
      }

      if (modulo.priceKey && this.responsavel[modulo.priceKey]) {
        additionalPrice = parseFloat(this.responsavel[modulo.priceKey]) || 0;
      }

      return {
        ...modulo,
        cost,
        additionalPrice,
      };
    },
    async carregarDados() {
      this.loading = true;
      try {
        const responsavelRes = await responsavelGeralDados(this.token);
        this.responsavel = responsavelRes?.data?.responsavel || {};
        this.precosPlanos = {
          0: parseFloat(this.responsavel.preco_plano_0) || 0,
          1: parseFloat(this.responsavel.preco_plano_1) || 0,
          2: parseFloat(this.responsavel.preco_plano_2) || 0,
          3: parseFloat(this.responsavel.preco_plano_3) || 0,
        };
      } catch (error) {
        this.setError(error?.response?.data?.message || "Erro ao carregar dados");
      } finally {
        this.loading = false;
      }
    },
    selecionarPlano(plano) {
      this.planoSelecionado = plano;
      this.$nextTick(() => {
        this.modulosSelecionados = [...this.modulosDoPlano];
      });
    },
    limparSelecao() {
      this.planoSelecionado = null;
      this.modulosSelecionados = [];
      this.taxas = { implantacao: 0, cadastro: 0 };
      this.cliente = { nome: "", tipo: "" };
    },
    calcularLucroPlano(plano) {
      const planoValue = plano.value;
      let custo = 0;

      this.allModules.forEach((modulo) => {
        let inclusoNoPlano = modulo.id === 'retaguarda';

        if (modulo.planKey && this.responsavel[modulo.planKey] !== undefined) {
          const planoDoModulo = parseInt(this.responsavel[modulo.planKey]) || 0;
          if (planoDoModulo > 0 && planoDoModulo <= planoValue) {
            inclusoNoPlano = true;
          }
        }

        if (inclusoNoPlano) {
          const moduloComValores = this.getModuloComValores(modulo);
          custo += moduloComValores.cost;
        }
      });

      const preco = this.precosPlanos[plano.value] || 0;
      return preco - custo;
    },
    imprimirProposta() {
      this.dialogProposta = true;
    },
    print() {
      const printContent = document.getElementById("proposta-print");
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Proposta Comercial</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h2, h3 { color: #1976D2; }
              table { width: 100%; border-collapse: collapse; margin: 10px 0; }
              td, th { padding: 8px; border-bottom: 1px solid #ddd; }
              ul { margin: 0; padding-left: 20px; }
              li { margin: 5px 0; }
              .text-right { text-align: right; }
              .font-weight-bold { font-weight: bold; }
            </style>
          </head>
          <body>${printContent.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onafterprint = function () {
        printWindow.close();
      };
      printWindow.print();
    },
  },
};
</script>

<style scoped>
.plano-card {
  transition: all 0.3s ease;
}

.plano-card:hover {
  transform: translateY(-4px);
}

.plano-ativo {
  border: 2px solid #1976D2 !important;
}

.categoria-header {
  font-size: 0.9em;
}

.border-primary {
  border: 2px solid #1976D2 !important;
}
</style>
